import { Form, Input, Modal } from 'antd';
import api from 'api';
import dayjs from 'dayjs';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import { TFunction } from 'i18next';
import { IDataRoom } from 'interfaces';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setRooms } from 'store/slices/userDataSlice';
import classes from './RoomAction.module.scss';
import { setDataRoom } from 'store/slices/dataRoomSlice';
import { setRoomAllChats } from 'store/slices/chatsSlice';
import RoomNameExistsModal from '../RoomNameExistsModal/RoomNameExistsModal';
import checkIsRoomNameExists from 'helpers/checkIsRoomNameExists';
import { normalizeWhitespace } from 'helpers/stringCaseManager';
import { handleInputChangeWithAltFilter, handlePasteWithAltFilter } from 'helpers/filterAltCodes';
import cn from 'classnames';
import { getTheme } from 'helpers/getTheme';
import { createSettingLog } from 'helpers/createSettingLog';

type Props = {
  room: IDataRoom;
  t: TFunction;
  isOpen: boolean;
  onCancel: () => void;
};

export default function EditRoomModal({ room, t, isOpen, onCancel }: Props) {
  const [fields, setFields] = useState({ name: room.name, description: room.description });
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isRoomNameExistsModalOpen, setIsRoomNameExistsModalOpen] = useState(false);
  const [form] = Form.useForm();
  
  const { available_rooms, userData } = useAppSelector((state) => state.userData);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { roomAllChats } = useAppSelector((state) => state.chat);
  const { messageApi } = useAppSelector((state) => state.windowState);
  
  const dispatch = useAppDispatch();


  const checkDuplicateAndOpenModal = (roomName: string) => {
    const isDuplicate = checkIsRoomNameExists(roomName, available_rooms, userData, room.id);
    if (isDuplicate) setIsRoomNameExistsModalOpen(true);
    return isDuplicate;
  }

  const onFinish = async (values: any) => {
    const isDuplicate = checkDuplicateAndOpenModal(values.name);
    if (isDuplicate) {
      return;
    }
    try {
      if(!(values.name as string).trim()) return;
      
      setIsEditLoading(true);
      const formData = new FormData();
      const normalizedRoomName = normalizeWhitespace(values.name);

      formData.append('name', normalizedRoomName);
      formData.append('description', values.description || '');
      formData.append('details', JSON.stringify(createSettingLog(room!, {...room!, name: normalizedRoomName, description: values.description})));

      const response = await api.setRoomSettings(formData, room.id, 'edit', '');
      const UTCDate = dayjs().valueOf() + new Date().getTimezoneOffset() * 60 * 1000;
      const newRooms = available_rooms.map((room) =>
        room.id === response.data.id
          ? {
            ...response.data,
            created_at: dayjs(response.data.created_at).unix() * 1000,
            last_action_timestamp: UTCDate,
            permissions: room.permissions,
            rooms_constraints: { ...room.rooms_constraints ,members_amount: room.rooms_constraints.members_amount },
            documents_amount: room.documents_amount,
            new_count: room.new_count,
            tariff: room.tariff,
            lives_to: room.lives_to,
            is_admin: room.is_admin,

          }
          : room
      ).sort((a,b) => b.last_action_timestamp - a.last_action_timestamp);
      dispatch(setRooms(newRooms || []));

      if (dataRoom?.id === response.data.id) {
        dispatch(setDataRoom({
          ...dataRoom!, name: response.data.name, description: response.data.description || ''
        }));
        dispatch(
          setRoomAllChats(
            roomAllChats.map((chat) =>
              chat.entity_type === 1 ? { ...chat, title: response.data.name, entity_name: response.data.name } : chat
            )
          )
        );
      }
      messageApi?.success(t('Rooms.success.edit'));
      onCancel();
    } catch (err) {
      messageApi?.error(t('Rooms.errors.edit'));
    } finally {
      setIsEditLoading(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      name: room.name,
      description: room.description,
    });
  }, []);

  const onInputsChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFields((prev) => {
      return {
        ...prev,
        [event.target.name]: event.target.value,
      };
    });

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleInputChangeWithAltFilter(event, (filteredValue) => {
      form.setFieldValue('name', filteredValue);
      setFields((prev) => ({ ...prev, name: filteredValue }));
    });
  };

  const handleNamePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    handlePasteWithAltFilter(event, fields.name, (filteredValue) => {
      form.setFieldValue('name', filteredValue);
      setFields((prev) => ({ ...prev, name: filteredValue }));
    });
  };

  return (
    <Modal
      title={t('Rooms.modals.edit.title')}
      className={cn(classes.modalWrap, getTheme())}
      width={'450px'}
      centered
      mask={{ closable: false }}
      open={isOpen}
      okText={t('Rooms.modals.edit.save')}
      cancelText={t('Rooms.modals.edit.cancel')}
      onCancel={onCancel}
      cancelButtonProps={{style: {display: 'none'}}}
      onOk={form.submit}
      okButtonProps={{
        disabled: room.name === fields.name && room.description === fields.description,
        loading: isEditLoading,
        style: {width: '100%', margin: 0}
      }}
    >
      <Form form={form} onFinish={onFinish} layout='vertical'>
        <Form.Item
          rules={[
            { required: true },
            { whitespace: true }
          ]}
          name={'name'}
          label={t('Settings.room.labels.name')}
          className={classes.item}
        >
          <Input
            placeholder={t('Settings.room.placeholders.name')}
            name='name'
            count={{
              show: true,
              max: 80,
            }}
            maxLength={80}
            value={fields.name}
            onChange={handleNameChange}
            onPaste={handleNamePaste}
          />
        </Form.Item>

        <Form.Item
          name={'description'}
          label={t('Settings.room.labels.description')}
          className={classes.item}
        >
          <Input.TextArea
            placeholder={t('Settings.room.placeholders.description')}
            name='description'
            autoSize={{minRows: 4, maxRows: 4}}
            value={fields.description}
            onChange={onInputsChange}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
      {isEditLoading && <UploadingComponent />}
      {isRoomNameExistsModalOpen && (
        <React.Suspense fallback={<div />}>
          <RoomNameExistsModal
            isOpen={isRoomNameExistsModalOpen}
            onCancel={() => setIsRoomNameExistsModalOpen(false)}
            name={fields.name}
          />
        </React.Suspense>
      )}
    </Modal>
  );
}
